/**
 * P1-E: 断点续跑面板 — 列出可恢复的 Agent 任务，支持恢复/删除
 * 轮询 /api/checkpoints/resumable
 */
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../services/apiClient';
import { RotateCcw, Trash2, Play, Loader2, AlertTriangle, CheckCircle2, Clock, History } from 'lucide-react';

interface CheckpointSummary {
  id: string;
  traceId: string;
  userId: string;
  agentId: string;
  taskDescription: string;
  status: 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  iteration: number;
  lastToolName: string;
  totalTokens: number;
  error: string;
  createdAt: string;
  updatedAt: string;
}

const STATUS_STYLE: Record<string, { color: string; icon: any; label: string }> = {
  running: { color: 'text-sky-300 border-sky-400/30 bg-sky-400/10', icon: Loader2, label: '运行中' },
  paused: { color: 'text-amber-300 border-amber-400/30 bg-amber-400/10', icon: Clock, label: '已暂停' },
  failed: { color: 'text-red-300 border-red-400/30 bg-red-400/10', icon: AlertTriangle, label: '失败' },
  completed: { color: 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10', icon: CheckCircle2, label: '已完成' },
  cancelled: { color: 'text-white/40 border-white/10 bg-white/5', icon: Clock, label: '已取消' },
};

const formatDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return iso;
  }
};

export function CheckpointPanel({ t }: { t: any }) {
  const [checkpoints, setCheckpoints] = useState<CheckpointSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resumingId, setResumingId] = useState<string>('');
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch('/api/checkpoints?limit=30', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `加载失败 (${res.status})`);
      setCheckpoints(data.checkpoints || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const handleResume = useCallback(async (id: string) => {
    setResumingId(id);
    setActionMsg('');
    try {
      const res = await apiFetch(`/api/checkpoints/${id}/resume`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `恢复失败 (${res.status})`);
      setActionMsg(`已开始恢复任务，使用 ${data.provider}/${data.model}`);
      await load();
    } catch (err: unknown) {
      setActionMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setResumingId('');
    }
  }, [load]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const res = await apiFetch(`/api/checkpoints/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `删除失败 (${res.status})`);
      }
      setActionMsg('已删除断点');
      await load();
    } catch (err: unknown) {
      setActionMsg(err instanceof Error ? err.message : String(err));
    }
  }, [load]);

  const resumable = checkpoints.filter(cp => ['running', 'paused', 'failed', 'cancelled'].includes(cp.status));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
          <History size={15} />
          {t?.checkpointTitle || '断点续跑'}
        </div>
        <div className="mt-1 text-[11px] text-white/40">
          {t?.checkpointDesc || '长任务中断后可从断点恢复继续执行'}
        </div>
        {actionMsg && <div className="mt-2 rounded-md border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] text-cyan-200">{actionMsg}</div>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 custom-scrollbar">
        {error && <div className="mb-3 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}
        {checkpoints.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-white/35">
            {loading ? '加载中...' : '暂无断点记录（多轮工具任务会自动保存断点）'}
          </div>
        ) : (
          <div className="space-y-2">
            {checkpoints.map(cp => {
              const style = STATUS_STYLE[cp.status] || STATUS_STYLE.paused;
              const Icon = style.icon;
              const canResume = ['paused', 'failed', 'cancelled'].includes(cp.status);
              return (
                <div key={cp.id} className="rounded-lg border border-white/8 bg-white/[0.03] px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${style.color}`}>
                          <Icon size={11} className={cp.status === 'running' ? 'animate-spin' : ''} />
                          {style.label}
                        </span>
                        <span className="text-[10px] text-white/30">迭代 {cp.iteration}</span>
                        {cp.totalTokens > 0 && <span className="text-[10px] text-white/30">{cp.totalTokens} tok</span>}
                      </div>
                      <div className="mt-1.5 truncate text-sm text-white/80">{cp.taskDescription || '(无描述)'}</div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] text-white/35">
                        {cp.lastToolName && <span>最后工具: {cp.lastToolName}</span>}
                        <span>{formatDateTime(cp.updatedAt)}</span>
                      </div>
                      {cp.error && <div className="mt-1 text-[11px] text-red-300">{cp.error}</div>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {canResume && (
                        <button
                          type="button"
                          onClick={() => void handleResume(cp.id)}
                          disabled={resumingId === cp.id}
                          className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1.5 text-[11px] font-bold text-cyan-200 transition-colors hover:bg-cyan-400/20 disabled:opacity-50"
                        >
                          {resumingId === cp.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                          {t?.resume || '恢复'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleDelete(cp.id)}
                        className="inline-flex items-center justify-center rounded-md border border-white/10 bg-white/5 p-1.5 text-white/45 transition-colors hover:bg-red-500/10 hover:text-red-300"
                        title={t?.delete || '删除'}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
